import {
  FieldType,
  useUpdateFieldMutation,
  type FullFieldFragment,
} from '@transactions/graphql';
import * as React from 'react';
import {Button} from 'src/Button';
import {
  FieldValuesInput,
  type FieldValueEntry,
} from 'src/FieldsView/FieldValuesInput';
import {Textarea} from 'src/inputs/Textarea';
import {TextInput} from 'src/inputs/TextInput';
import {Modal} from 'src/Modal';
import type {SystemProps} from 'src/Modals';

export type EditFieldModalUserProps = {
  field: FullFieldFragment;
  onSuccess: () => void;
};

export const EditFieldModal = ({
  onRequestClose,
  field,
  onSuccess,
}: EditFieldModalUserProps & SystemProps) => {
  const formId = React.useId();
  const [name, setName] = React.useState(field.name);
  const [description, setDescription] = React.useState(
    field.description || '',
  );
  const [values, setValues] = React.useState<FieldValueEntry[]>(
    field.values.map((v) => ({
      value: v.value,
      description: v.description || '',
    })),
  );
  const [required, setRequired] = React.useState(field.required);
  const [allowMultiple, setAllowMultiple] = React.useState(
    field.allowMultiple,
  );
  const [error, setError] = React.useState<string | null>(null);

  const isText = field.type === FieldType.Text;

  const [updateField, {loading}] = useUpdateFieldMutation();

  const hasChanges =
    name.trim() !== field.name ||
    description.trim() !== (field.description || '') ||
    (!isText &&
      (required !== field.required ||
        allowMultiple !== field.allowMultiple ||
        values.length !== field.values.length ||
        values.some(
          (v, i) =>
            v.value.trim() !== field.values[i]?.value ||
            v.description.trim() !== (field.values[i]?.description || ''),
        )));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      await updateField({
        variables: {
          id: field._id,
          name: name.trim(),
          description: description.trim() || null,
          ...(isText
            ? {}
            : {
                required,
                allowMultiple,
                values: values.map((v) => ({
                  value: v.value.trim(),
                  description: v.description.trim() || null,
                })),
              }),
        },
      });
      onSuccess();
      onRequestClose();
    } catch (e: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const err = e as Error;
      setError(err.message);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        type="submit"
        form={formId}
        disabled={
          loading ||
          !hasChanges ||
          !name.trim() ||
          !description.trim() ||
          (!isText && values.length === 0)
        }
      >
        {loading ? 'Saving...' : 'Save'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Edit Field"
      footer={footer}
      size="lg"
    >
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Name</label>
          <TextInput
            value={name}
            onChange={setName}
            placeholder="e.g., fulfillment, recurring"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Type</label>
          <div className="flex items-center gap-2">
            <span className="border-border bg-muted rounded-md border px-3 py-1.5 text-sm font-medium capitalize">
              {field.type}
            </span>
            <span className="text-muted-foreground text-xs">
              Type cannot be changed after creation
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Description</label>
          <Textarea
            value={description}
            onChange={setDescription}
            placeholder="Describe what this field tracks and how it should be used by the LLM (1-3 sentences)"
            rows={3}
          />
        </div>

        {!isText && (
          <>
            <FieldValuesInput
              values={values}
              onChange={setValues}
              onError={setError}
            />

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-required"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="edit-required" className="text-sm">
                  Required on all transactions
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-allowMultiple"
                  checked={allowMultiple}
                  onChange={(e) => setAllowMultiple(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="edit-allowMultiple" className="text-sm">
                  Allow multiple values
                </label>
              </div>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
};
